import { useState } from 'react'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Mail, Lock, User, CheckCircle, Loader2, AlertCircle, Package } from 'lucide-react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { cn } from '../../lib/utils'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Modal, AlertDialog } from '../../components/ui/Modal'
import { useAuthStore } from '../../stores/authStore'
import { useUIStore } from '../../stores/uiStore'
import toast from 'react-hot-toast'

const registerSchema = z.object({
  full_name: z.string().min(2, 'نام کامل باید حداقل ۲ کاراکتر باشد'),
  email: z.string().email('فرمت ایمیل نامعتبر است'),
  password: z.string().min(8, 'رمز عبور باید حداقل ۸ کاراکتر باشد'),
  confirmPassword: z.string(),
  tenant_name: z.string().min(2, 'نام شرکت/موسسه الزامی است'),
  tenant_slug: z.string().min(2, 'نام‌کاربری سازمان الزامی است').regex(/^[a-z0-9-]+$/, 'فقط حروف انگلیسی کوچک، اعداد و خط تیره مجاز است'),
  terms: z.boolean().refine(val => val === true, 'باید شرایط استفاده را بپذیرید'),
}).refine(data => data.password === data.confirmPassword, {
  message: 'رمز عبور و تکرار آن مطابقت ندارند',
  path: ['confirmPassword'],
})

type RegisterForm = z.infer<typeof registerSchema>

export default function Register() {
  const navigate = useNavigate()
  const { register: registerUser, isLoading: authLoading } = useAuthStore()
  const { modals, openModal, closeModal } = useUIStore()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const methods = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      full_name: '',
      email: '',
      password: '',
      confirmPassword: '',
      tenant_name: '',
      tenant_slug: '',
      terms: false,
    },
    mode: 'onChange',
  })

  const { register, handleSubmit, formState: { errors, isValid } } = methods

  const onSubmit = async (data: RegisterForm) => {
    setIsSubmitting(true)
    setError(null)

    try {
      await registerUser({
        full_name: data.full_name,
        email: data.email,
        password: data.password,
        tenant_name: data.tenant_name,
        tenant_slug: data.tenant_slug,
      })

      toast.success('ثبت‌نام با موفقیت انجام شد! به داشبورد منتقل می‌شوید...')
      navigate('/', { replace: true })
    } catch (err: any) {
      setError(err.response?.data?.detail || 'ثبت‌نام ناموفق بود. لطفاً مجدد تلاش کنید.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-950 px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary-600 mx-auto mb-4">
            <Package className="w-8 h-8 text-white" />
          </Link>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">ایجاد حساب کاربری</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-2">
            برای استفاده از Kaseb ثبت‌نام کنید
          </p>
        </div>

        {/* Register Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="card p-6"
        >
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            {error && (
              <AlertDialog
                isOpen={true}
                onClose={() => setError(null)}
                title="خطا در ثبت‌نام"
                description={error}
                variant="danger"
                confirmText="باشه"
              />
            )}

            <Controller
              name="full_name"
              control={methods.control}
              rules={{ required: true, minLength: 2 }}
              render={({ field }) => (
                <Input
                  label="نام کامل *"
                  placeholder="مثال: علی محمدی"
                  icon={<User className="w-5 h-5" />}
                  error={errors.full_name?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name="email"
              control={methods.control}
              rules={{ required: true, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ }}
              render={({ field }) => (
                <Input
                  label="ایمیل *"
                  type="email"
                  placeholder="user@company.com"
                  icon={<Mail className="w-5 h-5" />}
                  error={errors.email?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name="password"
              control={methods.control}
              rules={{ required: true, minLength: 8 }}
              render={({ field }) => (
                <Input
                  label="رمز عبور *"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="حداقل ۸ کاراکتر"
                  icon={<Lock className="w-5 h-5" />}
                  rightElement={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  }
                  error={errors.password?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name="confirmPassword"
              control={methods.control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  label="تکرار رمز عبور *"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="تکرار رمز عبور"
                  icon={<Lock className="w-5 h-5" />}
                  rightElement={
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  }
                  error={errors.confirmPassword?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name="tenant_name"
              control={methods.control}
              rules={{ required: true, minLength: 2 }}
              render={({ field }) => (
                <Input
                  label="نام شرکت/موسسه *"
                  placeholder="مثال: شرکت آریان تک"
                  icon={<User className="w-5 h-5" />}
                  error={errors.tenant_name?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name="tenant_slug"
              control={methods.control}
              rules={{ required: true, minLength: 2, pattern: /^[a-z0-9-]+$/ }}
              render={({ field }) => (
                <Input
                  label="نام‌کاربری سازمان *"
                  placeholder="مثال: my-company"
                  icon={<Package className="w-5 h-5" />}
                  error={errors.tenant_slug?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name="terms"
              control={methods.control}
              rules={{ required: true }}
              render={({ field }) => (
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 mt-0.5 rounded border-neutral-300 text-primary-600 focus:ring-2 focus:ring-primary-500/20"
                    {...field}
                  />
                  <span className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                    شرایط و قوانین <a href="/terms" className="text-primary-600 dark:text-primary-400 hover:underline">شرایط استفاده</a> و
                    <a href="/privacy" className="text-primary-600 dark:text-primary-400 hover:underline">حریم خصوصی</a> را مطالعه کرده و می‌پذیرم
                  </span>
                </label>
              )}
            />

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isSubmitting || authLoading || !isValid}
            >
              {isSubmitting || authLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  در حال ثبت‌نام...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  ایجاد حساب کاربری
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-neutral-500 dark:text-neutral-400">
              قبلاً حساب کاربری دارید؟{' '}
              <Link to="/login" className="text-primary-600 dark:text-primary-400 font-medium hover:underline">
                وارد شوید
              </Link>
            </p>
          </div>
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="mt-8 grid grid-cols-3 gap-4 text-center"
        >
          <div className="p-4 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
            <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mx-auto mb-2">
              <Lock className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <p className="text-xs font-medium text-neutral-900 dark:text-white">امنیت بالا</p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">رمزنگاری و احراز هویت دو مرحله‌ای</p>
          </div>
          <div className="p-4 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
            <div className="w-10 h-10 rounded-xl bg-success-100 dark:bg-success-900/30 flex items-center justify-center mx-auto mb-2">
              <CheckCircle className="w-5 h-5 text-success-600 dark:text-success-400" />
            </div>
            <p className="text-xs font-medium text-neutral-900 dark:text-white">پشتیبانی ۲۴/۷</p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">تیم پشتیبانی همیشه در خدمتون</p>
          </div>
          <div className="p-4 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
            <div className="w-10 h-10 rounded-xl bg-warning-100 dark:bg-warning-900/30 flex items-center justify-center mx-auto mb-2">
              <Package className="w-5 h-5 text-warning-600 dark:text-warning-400" />
            </div>
            <p className="text-xs font-medium text-neutral-900 dark:text-white">انبارداری کامل</p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">مدیریت موجودی، سفارش، فروش</p>
          </div>
        </motion.div>
      </motion.div>
    </div>
  )
}